<script>
  import './login/+page.css';
  import { onMount } from 'svelte';
  import { isAuthenticated, user, shouldAllowAutoSignIn } from '../stores/sessionStore.js';
  import { navigate as goto } from 'svelte-routing';
  import {
    API_BASE_URL,
    FRONTEND_URL,
    REDIRECT_URI,
    AUTH0_DOMAIN,
    AUTH0_CLIENT_ID,
    AUTH0_ORGANIZATION_ID
  } from '$lib/env.js';

  // Use the SPA SDK's loginWithRedirect (client-side flow) instead of the
  // legacy server-side /api/v1/auth/login endpoint.
  import { login as spaLogin } from '$lib/auth.js';
  
  // Get URL query parameters  
  let error = null;
  let errorMessage = null;
  let errorStatus = null;

  // Invitation parameters
  let invitation = null;
  let organizationId = null;
  let organizationName = null;
  let isInvitation = false;
  

  
  // Environment variables to display
  let envVars = {
    VITE_API_BASE_URL: API_BASE_URL,
    VITE_FRONTEND_URL: FRONTEND_URL,
    VITE_REDIRECT_URI: REDIRECT_URI,
    VITE_AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN,
    VITE_AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID,
    VITE_AUTH0_ORGANIZATION_ID: import.meta.env.VITE_AUTH0_ORGANIZATION_ID,
    NODE_ENV: import.meta.env.MODE || 'Not set'
  };

  const currentYear = new Date().getFullYear();

  const heroItems = [
    {
      title: 'CGM Data Capture',
      description: 'Continuous glucose monitoring data capture from CGM-compatible devices for centralized review and analysis',
      image: '/Participant Dashboard.jpg',
      alt: 'Participant dashboard overview'
    },
    {
      title: 'Wear Compliance',
      description: 'Wear compliance with AGP reports, time-in-range calculations, and trend analysis based on retrospective data',
      image: '/Participant CGM Wear Compliance.jpg',
      alt: 'CGM wear compliance view'
    },
    {
      title: 'On-Demand AGP Reports',
      description: 'Ambulatory Glucose Profile reports with glucose variability analysis and retrospective trend visualization',
      image: '/Participant Real Time AGP Report.jpg',
      alt: 'AGP report details'
    },
    {
      title: 'Study Management',
      description: 'Complete study lifecycle management from setup to completion with flexible configuration options',
      image: '/Site Participants List.jpg',
      alt: 'Study participant management list'
    },
    {
      title: 'Safety Monitoring',
      description: 'Proactive safety monitoring through data review tools, automated notifications, and subject profiling capabilities to support retrospective safety assessments',
      image: '/Participant Real Time CGM Measurements.jpg',
      alt: 'Real-time CGM measurements'
    },
    {
      title: 'Regulatory Ready',
      description: 'Built-in regulatory compliance features ensuring your studies meet global regulatory requirements and international standards',
      image: '/Participant CGM Baseline Compare Analysis.jpg',
      alt: 'Baseline compare analysis'
    }
  ];
  
  onMount(() => {
    console.log("LOGIN PAGE MOUNT: Checking for recent authentication attempts");
    // First check if we just came from Auth0 (force back to dashboard)
    const searchParams = new URLSearchParams(window.location.search);
        
    // Check for invitation parameters
    invitation = searchParams.get('invitation');
    organizationId = searchParams.get('organization');
    organizationName = searchParams.get('organization_name');
    isInvitation = !!invitation && !!organizationId;

    console.log('Invitation parameters:', { invitation, organizationId, organizationName, isInvitation });
    
    // If this is an invitation, handle it immediately
    if (isInvitation) {
      console.log('Detected invitation link, proceeding to Auth0 login with invitation');
      handleInvitationLogin();
      return;
    }

    // Don't do any redirection if coming from Auth0 - let the dashboard handle it
    const url = new URL(window.location.href);
    if (url.searchParams.has('userData')) {
      console.log('Login detected userData in params, this should not happen - we should be on dashboard');
      window.location.href = '/dashboard' + window.location.search;
      return;
    }
    
    // Check if auto sign-in should be allowed
    if (!shouldAllowAutoSignIn()) {
      console.log('LOGIN PAGE: Auto sign-in blocked due to recent logout');
      return;
    }

    // If already logged in according to the store, redirect to dashboard
    if ($isAuthenticated && $user) {
      console.log('Already authenticated according to store, redirecting to dashboard');
      goto('/dashboard?fromLogin=true');
      return;
    }
    
    // Check for error parameters (only if not already set by timeout)
    if (!error) {
      error = url.searchParams.get('error');
      errorMessage = url.searchParams.get('message');
      errorStatus = url.searchParams.get('status');
    }
    
    console.log('Login page loaded', { error, errorMessage, errorStatus });
  });
  
  async function handleLogin() {
    // Drop any stale client-side session bits so the SDK starts clean.
    clearAuth0Cookies();
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    user.set(null);
    isAuthenticated.set(false);

    // Strip error params from the URL before redirecting away.
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('message');
    url.searchParams.delete('status');
    window.history.replaceState({}, document.title, url.pathname);

    // Hand off to the SPA SDK. It navigates the window to
    // https://<tenant>/authorize?... and Auth0 redirects back to our
    // origin (http://localhost:5173 in dev) where App.svelte's
    // handleRedirectCallback() processes the code grant.
    //
    // The legacy `${API_BASE_URL}/auth/login?returnTo=...` server-flow
    // is intentionally NOT used — Spring is a stateless resource server
    // and doesn't run the OAuth code flow on the server.
    try {
      await spaLogin('/dashboard');
    } catch (e) {
      console.error('Error starting Auth0 login redirect:', e);
      alert('Error starting sign-in. Check the console for details.');
    }
  }

  async function handleInvitationLogin() {
    clearAuth0Cookies();
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    user.set(null);
    isAuthenticated.set(false);

    // Hand off to the SPA SDK with the invitation + organization params.
    // The SDK forwards them to /authorize as ?invitation=...&organization=...
    // which Auth0's invitation flow expects.
    //
    // The legacy `${API_BASE_URL}/auth/direct-invitation?...` endpoint
    // ran server-side; not used here for the same reason as handleLogin.
    try {
      const { getClient } = await import('$lib/auth.js');
      const client = await getClient();
      await client.loginWithRedirect({
        appState: { returnTo: '/dashboard' },
        authorizationParams: {
          redirect_uri: window.location.origin,
          invitation,
          organization: organizationId
        }
      });
    } catch (e) {
      console.error('Error starting Auth0 invitation login:', e);
      alert('Error starting sign-in via invitation. Check the console for details.');
    }
  }
  
  // Add function to clear Auth0 cookies
  function clearAuth0Cookies() {
    // Clear any potential Auth0 cookies
    let domain;
    if (AUTH0_DOMAIN) {
      domain = AUTH0_DOMAIN.split('.').slice(-2).join('.');
    } else {
      console.warn('AUTH0_DOMAIN is undefined, skipping domain-specific cookie clearing');
      domain = null;
    }
    document.cookie = `auth0.is.authenticated=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;${domain ? ` domain=.${domain}` : ''}`;
    document.cookie = `auth0.is.authenticated=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    
    // Clear common Auth0 cookies
    const commonCookies = [
      'auth0.is.authenticated',
      'auth0_compat',
      'auth0',
      'auth0_ss',
      'auth0_ss_compat',
      'did',
      'did_compat'
    ];
    
    commonCookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      if (domain) {
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${domain}`;
      }
    });
    
    console.log('Attempted to clear Auth0 cookies');
  }
</script>

<div class="login-page min-h-screen flex flex-col">
  <main class="flex-1 flex items-center justify-center px-6 py-16">
    <div class="text-center">
      <img src="/summa-logo-big.png" alt="SUMMA Portal" class="h-20 mx-auto mb-4" />
      <h1 class="text-3xl sm:text-4xl font-semibold text-white mb-0">SUMMA</h1>
      <h2 class="text-xl sm:text-xl font-semibold text-white mb-4">Global CGM Data Management Solutions</h2>
      <h3 class="text-sm sm:text-sm font-light text-white/80 mb-2 px-2.5 md:px-40">Transform your clinical trials with continuous glucose monitoring data capture and advanced analytics. The SUMMA Portal delivers insights from multiple CGM devices, supporting study oversight, analysis, and protocol-driven decisions to improve study outcomes.</h3>
      <h3 class="text-sm sm:text-sm font-light text-white/80 mb-8 px-2.5 md:px-40">Advanced continuous glucose monitoring with data capture and analytics for clinical research. Monitor operational metrics to ensure wear compliance with protocol-driven requirements.</h3>
      <button
        on:click={handleLogin}
        class="border-2 border-white/50 hover:bg-white hover:text-slate-600 text-white px-8 py-3 rounded-lg font-medium"
      >
        Sign In
      </button>

      <div class="mt-10 w-full max-w-6xl mx-auto text-left">
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {#each heroItems as item (item.title)}
            <article class="rounded-2xl border border-white/10 bg-white/5 p-4">
              <img
                src={item.image}
                alt={item.alt}
                class="h-50 w-full rounded-md object-cover"
                loading="lazy"
              />
              <h4 class="mt-4 text-lg font-semibold text-white">{item.title}</h4>
              <p class="mt-2 text-sm text-white/70">{item.description}</p>
            </article>
          {/each}
        </div>
      </div>
    </div>
  </main>

  <section class="w-full px-6 pb-8">
    <p class="text-white/40 text-sm text-center">
      © {currentYear}
      <a
        href="https://precisiondigitalhealth.com"
        target="_blank"
        class="text-white/40 hover:text-white hover:underline hover:underline-offset-2"
      >
        Precision Digital Health, Inc.
      </a>
      All rights reserved.
    </p>
  </section>
</div>
